<script setup>
import { ref, reactive, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { Search as SearchIcon } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import { getTemplateList as fetchTemplateList } from "@/entities/template";
import dateFormatUtil from "@/shared/lib/date-format";
import { getDirectoryItemsByCodes } from "@/entities/directory";
import {
  ConstructorStatusColor,
  ConstructorStatusLabel,
} from "@/shared/config/constructor";
import Button from "@/shared/ui/button/Button.vue";
import TableRowActions from "@/shared/ui/custom/components/TableRowActions.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const loading = ref(false);
const list = ref([]);
const total = ref(0);
const filters = reactive({
  q: "",
  page: 1,
  page_size: 20,
});
const references = ref([]);

const actions = computed(() => {
  const list = [];
  if (store.getters.has_access("TDT")) {
    list.push({
      label: t("Деактивировать"),
      type: "danger",
      action: "deactivate",
      icon: "huge-close",
    });
  }
  if (store.getters.has_access("TUT")) {
    list.push({
      label: t("Редактировать"),
      type: "default",
      action: "edit",
      icon: "huge-pen2",
    });
  }
  list.push({
    label: t("Просмотр действия"),
    type: "default",
    action: "view",
    icon: "huge-eye",
    isLast: true,
  });
  return list;
});

const canCreate = computed(() => store.getters.has_access("TCT"));

async function getTemplateList() {
  let params = {
    page_size: filters.page_size,
    page: filters.page,
  };
  if (filters.q) {
    params.q = filters.q.trim().toLowerCase();
  }
  loading.value = true;
  try {
    let data = await fetchTemplateList(params);
    if (data?.success) {
      if (data.payload) {
        total.value = data.payload?.total;
        filters.page_size = data.payload?.page_size;
        filters.page = data.payload?.page;
        list.value = data.payload.items || [];
      }
      if (data.payload.items?.length) {
        let category_codes = list.value.map((item) => item.category_code);
        let notary_codes = list.value.map((item) => item.code);
        let codes = [...new Set([...category_codes, ...notary_codes])];
        await fetchDirectoryItemsByCodes(codes);
      }
    }
  } finally {
    loading.value = false;
  }
}

async function fetchDirectoryItemsByCodes(codes) {
  loading.value = true;
  try {
    if (!codes || !codes.length) return;
    let data = await getDirectoryItemsByCodes({ codes: codes });
    if (data?.success) {
      if (data.payload.items?.length) {
        references.value = data.payload.items;
      }
    }
  } finally {
    loading.value = false;
  }
}

function handlePageChange(page) {
  filters.page = page;
}

function goToCreate() {
  router.push({
    name: "constructor_action_new",
    params: { id: "new" },
  });
}

function dateFormat(date) {
  if (!date) {
    return t("Не указано");
  }
  return dateFormatUtil.timestamp_to_local_tz(date, "DD.MM.YYYY, HH:mm");
}

function getStatusLabel(item) {
  return (
    ConstructorStatusLabel[item.status] ||
    item.status?.label ||
    item.status?.name ||
    item.status ||
    t("Не указано")
  );
}

function getStatusColor(item) {
  return ConstructorStatusColor[item.status];
}

async function handleActions(item) {
  if (item.action === "edit") {
    router.push({
      name: "constructor_action_item",
      params: { id: item.code },
    });
  } else if (item.action === "view") {
    router.push({
      name: "constructor_action_item_view",
      params: { id: item.code },
    });
  }
}

function handleRowClick(row) {
  const canEdit = store.getters.has_access("TUT");

  router.push({
    name: canEdit ? "constructor_action_item" : "constructor_action_item_view",
    params: { id: row.code },
  });
}

function search() {
  filters.page = 1;
}

function getReferenceTitle(code) {
  if (!code) {
    return t("Не указано");
  }
  if (references.value?.length === 0) return code;
  let reference = references.value.find((item) => item.code === code);
  return reference?.name || code;
}

watch(
  filters,
  () => {
    getTemplateList();
  },
  { deep: true }
);

onMounted(() => {
  getTemplateList();
});
</script>

<template>
  <div class="wrapper-page">
    <div class="flex flex-col h-full w-full gap-8">
      <div class="flex justify-between gap-5">
        <div class="relative w-80">
          <SearchIcon
            class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none"
          />
          <Input
            v-model="filters.q"
            :placeholder="$t('Поиск по действиям')"
            class="pl-9"
            @input="search"
          />
        </div>
        <Button v-if="canCreate" variant="default" @click="goToCreate"
          >{{ $t("Создать действие") }} +
        </Button>
      </div>

      <div class="table-area">
        <div class="h-full overflow-auto">
          <Table :loading="loading" :scroll-reset-key="filters.page">
            <TableHeader>
              <TableRow>
                <TableHead class="max-w-[68px]"></TableHead>
                <TableHead class="min-w-[294px]">{{
                  $t("Нотариальное действие")
                }}</TableHead>
                <TableHead class="min-w-[294px]">{{
                  $t("Категория нотариального действия")
                }}</TableHead>
                <TableHead class="min-w-[220px]">{{
                  $t("Дата создания")
                }}</TableHead>
                <TableHead class="min-w-[220px]">{{
                  $t("Дата изменения")
                }}</TableHead>
                <TableHead class="min-w-[220px]">{{ $t("Статус") }}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableEmpty v-if="!loading && !list.length" :colspan="6">
                {{ $t("Нет данных") }}
              </TableEmpty>
              <TableRow
                v-for="row in list"
                :key="row.code"
                class="cursor-pointer"
                @click="handleRowClick(row)"
              >
                <TableCell>
                  <TableRowActions
                    :actions="actions"
                    @action="
                      (item) =>
                        handleActions({ action: item.action, code: row.code })
                    "
                  />
                </TableCell>
                <TableCell>
                  <div v-if="row.code">
                    {{ getReferenceTitle(row.code) }}
                  </div>
                  <div v-else>
                    {{ $t("Не указано") }}
                  </div>
                </TableCell>
                <TableCell>
                  <div v-if="row.category_code">
                    {{ getReferenceTitle(row.category_code) }}
                  </div>
                  <div v-else>
                    {{ $t("Не указано") }}
                  </div>
                </TableCell>
                <TableCell>{{ dateFormat(row.created_at) }}</TableCell>
                <TableCell>{{ dateFormat(row.changed_at) }}</TableCell>
                <TableCell>
                  <div class="flex items-center justify-start gap-2">
                    <span :class="getStatusColor(row)" class="w-2 h-2"></span>
                    {{ getStatusLabel(row) }}
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <TableFooterWrapper
          :total="total"
          v-model:pageSize="filters.page_size"
          v-model:currentPage="filters.page"
          :pageSizes="[1, 5, 10, 20]"
          :page-size="filters.page_size"
        >
          <template #pagination>
            <Pagination
              v-if="total > filters.page_size"
              :total="total"
              :items-per-page="filters.page_size"
              :page="filters.page"
              :sibling-count="1"
              :disabled="loading"
              show-edges
              @update:page="handlePageChange"
            >
              <PaginationContent v-slot="{ items }">
                <PaginationPrevious />
                <template v-for="(item, index) in items" :key="index">
                  <PaginationItem
                    v-if="item.type === 'page'"
                    :value="item.value"
                    :is-active="item.value === filters.page"
                  />
                  <PaginationEllipsis v-else />
                </template>
                <PaginationNext />
              </PaginationContent>
            </Pagination>
          </template>
        </TableFooterWrapper>
      </div>
    </div>
    <router-view></router-view>
  </div>
</template>

<style scoped lang="scss">
.next {
  @apply gap-4 border border-border;
  display: flex;
  padding: 4px 4px 4px 8px;
  align-items: center;
  justify-content: center;
  height: 32px;
  border-radius: 8px;
  background-color: hsl(var(--muted));
}
.arrow-right {
  @apply border border-border;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px !important;

  height: 24px;
  border-radius: 4px;
  background-color: hsl(var(--card));

  svg {
    width: 12px;
    height: 12px;
  }
}
</style>
